import { useEffect, useState } from "react";
import { Leaf, Heart, Plane } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";

const imgSrc = (img) =>
  img && (img.startsWith("/api/") || img.startsWith("http")) ? mediaUrl(img) : img;

const ICONS = { leaf: Leaf, heart: Heart, plane: Plane };

// Hard-coded fallback so the page ALWAYS renders even if the API/DB is unavailable.
const DEFAULTS = {
  hero_title: "Sew Voyage",
  hero_script: "Our Story",
  tagline_caps: "TWO WOMEN. ONE VISION.",
  tagline_script: "A love for fashion & travel.",
  intro: [
    "We're Tanisha and Kat, the women behind Sew Voyage.",
    "Our story started in a fashion class, where we met through our shared love for style and design. Tanisha, a designer and seamstress who has been sewing since childhood, began teaching Kat how to sew. Kat, a model with a love for fashion and travel, had always dreamed of creating a brand of her own.",
    "What started with sewing lessons grew into conversations, ideas, sketches, fabrics, and eventually a vision we both believed in.",
  ],
  intro_script: "That vision became Sew Voyage.",
  note1_script: "We believe beautiful fashion doesn't always have to start with something new.",
  s1_title: "FROM WASTE TO VOYAGE",
  s1_body: [
    "We source deadstock and rescued fabrics\u2014beautiful materials left unused from previous production, canceled orders, and excess inventory\u2014and reimagine them into limited resort pieces designed for vacations, celebrations, and unforgettable destinations.",
    "Working with deadstock means we don't always know what beautiful fabric we'll discover next\u2014and that's part of the magic. A fabric may only be available in enough yardage to create a handful of pieces. Once it's gone, it may truly be gone.",
  ],
  s1_script: "That allows us to create collections that feel special, intentional, and unique.",
  s2_title: "BUILT TOGETHER",
  s2_body: [
    "Sew Voyage has been a true team effort. We are learning, designing, creating, traveling, dreaming, and building this brand together. We each bring something different to the table, and that's what makes Sew Voyage ours.",
    "For us, this is bigger than clothing. It's about two women taking what we're passionate about and having the courage to build something of our own. It's about giving overlooked fabrics another opportunity to become beautiful. And it's about creating pieces that make women feel confident, sexy, effortless, and unforgettable wherever their journey takes them.",
  ],
  note2_script: "We turn waste into something purposeful.",
  values: [
    { label: "CONSCIOUS BY CHOICE", icon: "leaf" },
    { label: "LUXURIOUS BY NATURE", icon: "heart" },
    { label: "YOURS TO VOYAGE", icon: "plane" },
  ],
  closing_script: "When you wear Sew Voyage, you're not simply wearing resort wear. You're wearing something rescued. Something reimagined. Something created with purpose. And something made to travel with you.",
  images: {
    logo_tag: "/ourstory/logo_tag.png",
    sewing: "/ourstory/sewing.png",
    jumpsuit: "/ourstory/jumpsuit.png",
    deadstock: "/ourstory/deadstock.png",
    walking: "/ourstory/walking.png",
    thread: "/ourstory/thread.png",
  },
};

export default function OurStory() {
  const [d, setD] = useState(DEFAULTS);

  useEffect(() => {
    document.title = "Our Story · Sew Voyage";
    api.get("/our-story").then(({ data }) => {
      if (data && typeof data === "object") {
        setD({ ...DEFAULTS, ...data, images: { ...DEFAULTS.images, ...(data.images || {}) } });
      }
    }).catch(() => {});
  }, []);

  const Body = ({ children }) => (
    <p className="font-heading text-[1.02rem] leading-relaxed text-ink/80 mb-4">{children}</p>
  );
  const Script = ({ children, className = "" }) => (
    <p className={`font-script text-2xl text-ink/80 ${className}`}>{children}</p>
  );

  return (
    <div
      data-testid="our-story-page"
      className="min-h-screen"
      style={{ background: "radial-gradient(ellipse 120% 80% at 50% 15%, #ece0c8 0%, #e2d2b4 48%, #ccb996 82%, #bfab86 100%)" }}
    >
      <div className="mx-auto max-w-6xl px-5 md:px-10 py-14 md:py-20">

        {/* ===== HEADER BAND ===== */}
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-3 flex justify-center lg:block">
            <img src={imgSrc(d.images?.logo_tag)} alt="Sew Voyage tag" className="w-40 lg:w-full max-w-[220px]" data-testid="os-img-logo_tag" />
          </div>

          <div className="lg:col-span-5 text-center lg:pt-6">
            <h1 className="font-heading tracking-[0.12em] text-5xl md:text-6xl text-ink leading-none" data-testid="os-hero-title">{d.hero_title}</h1>
            <p className="font-script text-6xl md:text-7xl text-ink mt-1" data-testid="os-hero-script">{d.hero_script}</p>
            <Heart size={16} className="mx-auto my-4 text-ink/50" fill="currentColor" />
            <h2 className="font-heading uppercase tracking-[0.22em] text-lg md:text-xl text-ink" data-testid="os-tagline-caps">{d.tagline_caps}</h2>
            <span className="inline-block font-script text-2xl md:text-3xl text-ink mt-2 bg-[#d8b98a]/50 px-3 py-0.5" data-testid="os-tagline-script">{d.tagline_script}</span>

            <div className="text-left mt-8 max-w-md mx-auto">
              {(d.intro || []).map((p, i) => <Body key={i}>{p}</Body>)}
              <Script className="mt-2">{d.intro_script}</Script>
            </div>
          </div>

          <div className="lg:col-span-4">
            <img src={imgSrc(d.images?.sewing)} alt="Founders sewing" className="w-full" data-testid="os-img-sewing" />
            <Script className="text-right mt-4 leading-snug pr-2">{d.note1_script}</Script>
          </div>
        </div>

        {/* ===== MIDDLE BAND ===== */}
        <div className="grid lg:grid-cols-12 gap-8 items-start mt-4 lg:mt-2">
          <div className="lg:col-span-4 order-2 lg:order-1">
            <img src={imgSrc(d.images?.jumpsuit)} alt="Resort jumpsuit" className="w-full max-w-[360px] mx-auto" data-testid="os-img-jumpsuit" />
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2 lg:pt-8">
            <h3 className="font-heading uppercase tracking-[0.18em] text-2xl text-ink mb-4" data-testid="os-s1-title">{d.s1_title}</h3>
            {(d.s1_body || []).map((p, i) => <Body key={i}>{p}</Body>)}
            <Script className="mt-2 mb-10 leading-snug">{d.s1_script}</Script>

            <h3 className="font-heading uppercase tracking-[0.18em] text-2xl text-ink mb-4" data-testid="os-s2-title">{d.s2_title}</h3>
            {(d.s2_body || []).map((p, i) => <Body key={i}>{p}</Body>)}
          </div>

          <div className="lg:col-span-3 order-3 space-y-8">
            <img src={imgSrc(d.images?.deadstock)} alt="Rescued deadstock fabric" className="w-full max-w-[260px] mx-auto" data-testid="os-img-deadstock" />
            <img src={imgSrc(d.images?.walking)} alt="Founders walking" className="w-full max-w-[300px] mx-auto" data-testid="os-img-walking" />
          </div>
        </div>

        {/* ===== FOOTER BAND ===== */}
        <div className="grid lg:grid-cols-12 gap-8 items-center mt-6 border-t border-ink/15 pt-12">
          <div className="lg:col-span-4 text-center lg:text-left">
            <Script className="mb-4">{d.note2_script}</Script>
            <img src={imgSrc(d.images?.thread)} alt="Rescued thread" className="w-40 mx-auto lg:mx-0" data-testid="os-img-thread" />
          </div>

          <div className="lg:col-span-4 flex items-stretch justify-center gap-0 divide-x divide-ink/20" data-testid="os-values">
            {(d.values || []).map((v, i) => {
              const Icon = ICONS[v.icon] || Leaf;
              return (
                <div key={i} className="flex flex-col items-center text-center px-4 md:px-6">
                  <Icon size={26} strokeWidth={1.3} className="text-ink/70 mb-3" />
                  <p className="font-body uppercase tracking-wide2 text-[0.58rem] text-ink/70 leading-tight whitespace-pre-line">{v.label.replace(/ /g, "\n")}</p>
                </div>
              );
            })}
          </div>

          <div className="lg:col-span-4">
            <div className="bg-[#e7d9bd]/60 border border-ink/15 px-6 py-6 -rotate-1 shadow-sm">
              <Script className="text-center italic leading-relaxed text-[1.15rem]" data-testid="os-closing">{d.closing_script}</Script>
              <Heart size={14} className="mx-auto mt-3 text-ink/40" fill="currentColor" />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
